/**
 * CombatSystem.ts — High-level combat orchestrator.
 *
 * Manages combat lifecycle: initialization, round resolution, result application.
 * Combat is transient — CombatSystem owns no persistent state. Results are
 * applied back to GameState when combat ends.
 */

import type { GameState } from '@core/engine/GameState';
import type { EventSystem } from '@core/engine/EventSystem';
import type {
  CombatAction,
  CombatantState,
  CombatAbility,
  CombatResult,
  EnemyDefinition,
  RoundResult,
  CombatOutcome,
} from '@core/combat/CombatTypes';
import {
  resolveRound,
  resolveFlee,
  derivePlayerAttack,
  derivePlayerDefense,
  checkCombatEnd,
  buildCombatResult,
} from '@core/combat/CombatResolver';
import { getInitialStance } from '@core/combat/StanceSystem';
import { getEnemyAction } from '@core/combat/EnemyAI';
import { getClassCombatAbilities } from '@core/combat/AbilityBridge';
import { getClassAbilities } from '@game/data/abilities';
import type { SkillSystem } from './SkillSystem';

// ─── Combat Session ─────────────────────────────────────────────

export interface CombatSession {
  player: CombatantState;
  enemy: CombatantState;
  enemyDef: EnemyDefinition;
  round: number;
  playerBaseAttack: number;
  playerBaseDefense: number;
  abilities: CombatAbility[];
  startHP: number;
  startFrostbite: number;
  roundHistory: RoundResult[];
}

// ─── CombatSystem ───────────────────────────────────────────────

export class CombatSystem {
  private events: EventSystem;
  private skillSystem: SkillSystem | null = null;

  constructor(events: EventSystem) {
    this.events = events;
  }

  /** Wire a SkillSystem so combat includes skill tree bonuses. */
  setSkillSystem(skillSystem: SkillSystem): void {
    this.skillSystem = skillSystem;
  }

  /**
   * Initialize a combat session from game state and enemy definition.
   * Does NOT modify GameState — returns a transient session.
   */
  initCombat(state: GameState, enemyDef: EnemyDefinition): CombatSession {
    const classAbilities = getClassAbilities(state.character.classId);
    const primaryStat = classAbilities?.primaryStat ?? 'strength';

    let playerAttack = derivePlayerAttack(
      state.character.stats as unknown as Record<string, number>,
      primaryStat,
    );
    let playerDefense = derivePlayerDefense(
      state.character.stats as unknown as Record<string, number>,
    );

    // Apply skill tree bonuses
    if (this.skillSystem) {
      playerAttack += this.skillSystem.getTotalBonus('attack_bonus', state);
      playerDefense += this.skillSystem.getTotalBonus('defense_bonus', state);
    }

    const abilities = getClassCombatAbilities(state.character.classId, state.abilityTiers);

    return {
      player: {
        name: state.character.name,
        hp: state.character.hp,
        hpMax: state.character.hpMax,
        stance: getInitialStance(),
        effects: [],
      },
      enemy: {
        name: enemyDef.name,
        hp: enemyDef.hp,
        hpMax: enemyDef.hp,
        stance: getInitialStance(),
        effects: [],
      },
      enemyDef,
      round: 0,
      playerBaseAttack: playerAttack,
      playerBaseDefense: playerDefense,
      abilities,
      startHP: state.character.hp,
      startFrostbite: state.character.frostbite,
      roundHistory: [],
    };
  }

  /**
   * Execute one round of combat.
   * Returns the round result. Call checkEnd() after to see if combat is over.
   */
  executeRound(
    session: CombatSession,
    playerAction: CombatAction,
  ): RoundResult {
    session.round++;

    // Get enemy decision
    const enemyActionType = getEnemyAction(
      session.enemyDef.behavior,
      session.enemy,
      session.player,
      session.round,
    );
    const enemyAction: CombatAction = { type: enemyActionType };

    // Resolve ability if used
    let ability: CombatAbility | undefined;
    if (playerAction.type === 'ability' && playerAction.targetId) {
      ability = session.abilities.find(a => a.id === playerAction.targetId);
    }

    // Resolve the round
    const result = resolveRound({
      round: session.round,
      player: session.player,
      enemy: session.enemy,
      playerAction,
      enemyAction,
      playerAttack: session.playerBaseAttack,
      playerDefense: session.playerBaseDefense,
      enemyDef: session.enemyDef,
      ability,
    });

    // Update session state
    session.player.hp = result.playerHP;
    session.player.stance = result.playerStance;
    session.enemy.hp = result.enemyHP;
    session.enemy.stance = result.enemyStance;

    session.roundHistory.push(result);

    return result;
  }

  /**
   * Attempt to flee from combat.
   * Returns true if successful.
   */
  attemptFlee(session: CombatSession): { fled: boolean; roundResult: RoundResult } {
    session.round++;

    const enemyActionType = getEnemyAction(
      session.enemyDef.behavior,
      session.enemy,
      session.player,
      session.round,
    );
    const enemyAction: CombatAction = { type: enemyActionType };
    const playerAction: CombatAction = { type: 'flee' };

    const fled = resolveFlee(session.player.stance, enemyAction);

    // If flee fails, enemy still gets their action
    const result = resolveRound({
      round: session.round,
      player: session.player,
      enemy: session.enemy,
      playerAction,
      enemyAction,
      playerAttack: session.playerBaseAttack,
      playerDefense: session.playerBaseDefense,
      enemyDef: session.enemyDef,
    });

    // Update session state
    session.player.hp = result.playerHP;
    session.player.stance = result.playerStance;
    session.enemy.hp = result.enemyHP;
    session.enemy.stance = result.enemyStance;

    if (fled) {
      result.narrative = 'You break away from combat! ' + result.narrative;
    } else {
      result.narrative = 'Your escape attempt fails! ' + result.narrative;
    }

    session.roundHistory.push(result);

    return { fled, roundResult: result };
  }

  /**
   * Check if combat should end. Returns outcome or null if still fighting.
   */
  checkEnd(session: CombatSession, fled: boolean = false): CombatOutcome | null {
    // Calculate cumulative frostbite
    const totalFrostbite = session.startFrostbite +
      session.roundHistory.reduce((sum, r) => sum + r.playerFrostbiteGain, 0);

    return checkCombatEnd(
      session.player.hp,
      session.enemy.hp,
      totalFrostbite,
      fled,
    );
  }

  /**
   * Build the final combat result and apply consequences to GameState.
   */
  finalizeCombat(
    session: CombatSession,
    outcome: CombatOutcome,
    state: GameState,
  ): CombatResult {
    const totalFrostbite = session.roundHistory.reduce((sum, r) => sum + r.playerFrostbiteGain, 0);
    const hpLost = session.startHP - session.player.hp;

    const result = buildCombatResult(
      outcome,
      session.round,
      hpLost,
      totalFrostbite,
      session.enemyDef,
    );

    // Apply results to GameState
    state.character.hp = Math.max(0, session.player.hp);
    state.character.frostbite = Math.min(100, state.character.frostbite + totalFrostbite);

    if (outcome === 'victory') {
      state.modifyStat('experience_points', result.xpEarned);
      state.modifyStat('gold', result.goldEarned);
      for (const itemId of result.lootDropped) {
        state.addItem(itemId);
      }
      // Increment combat counter for achievements
      const victories = (state.achievementCounters.get('combat_victories') ?? 0) + 1;
      state.achievementCounters.set('combat_victories', victories);
    } else if (outcome === 'defeat') {
      const deaths = (state.achievementCounters.get('deaths') ?? 0) + 1;
      state.achievementCounters.set('deaths', deaths);
    }

    this.events.emit('combatEnded', result);

    return result;
  }
}
